import type { ConditionField } from '@/components/ui';
import type {
  Condition,
  ConditionOperator,
  ConditionTree,
} from '@/api/queries/workspace';

// A small Loki-style query language that compiles to the same ConditionTree the
// visual builder edits, so the raw box and the builder stay interchangeable.
//
//   email:john              email contains "john"
//   email=john@x.cz         email equals
//   lastName:^No            starts with
//   lastName:$ak            ends with
//   status.active=true      equals
//   yearOfStudy>2           greater than
//   audit.updatedAt<=2024-01-01   before
//   tags:(vip|customer)     is one of
//   audit.createdAt:[a..b]  between a and b
//   phone:*                 is set
//   middleName:_            is empty
//
// Conditions are separated by whitespace and combined with AND (default) or OR.

const EMPTY_TREE: ConditionTree = { conjunction: 'and', conditions: [] };

const OP_TO_SYMBOL: Record<ConditionOperator, string> = {
  equals: '=',
  not_equals: '!=',
  contains: ':',
  starts_with: ':^',
  ends_with: ':$',
  gt: '>',
  lt: '<',
  after: '>=',
  before: '<=',
  in: ':(',
  not_in: '!:(',
  between: ':[',
  is_set: ':*',
  is_empty: ':_',
};

/** Builds the lookup that resolves a user-typed field token to a Mongo path. */
function fieldResolver(fields: ConditionField[]): (token: string) => string {
  const map = new Map<string, string>();
  for (const f of fields) {
    map.set(f.value.toLowerCase(), f.value);
    map.set(f.label.toLowerCase(), f.value);
  }
  // A couple of friendly aliases on top of the field labels.
  map.set('name', 'search.nameAscii');
  map.set('email', 'contact.primaryEmail');
  map.set('phone', 'contact.phones');
  map.set('tag', 'tags');
  map.set('source', 'sources');
  map.set('product', 'services.serviceId');
  map.set('serviceid', 'services.serviceId');
  map.set('country', 'addresses.country');
  map.set('dateofbirth', 'profile.dateOfBirth');
  map.set('dob', 'profile.dateOfBirth');
  map.set('faculty', 'education.faculty');
  map.set('institution', 'education.institution');
  map.set('field', 'education.field');
  map.set('studylocation', 'education.location');
  map.set('yearofstudy', 'education.yearOfStudy');
  return (token: string) => map.get(token.toLowerCase()) ?? token;
}

/** Splits a query into tokens, keeping quoted runs ("a b") together. */
function tokenize(raw: string): string[] {
  const tokens: string[] = [];
  const re = /[^\s"]*"[^"]*"[^\s"]*|[^\s]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) tokens.push(m[0]);
  return tokens;
}

function unquote(v: string): string {
  return v.replace(/"([^"]*)"/g, '$1');
}

/** Parses one `field<op>value` token into a Condition. */
function parseToken(token: string, resolve: (t: string) => string): Condition | null {
  // Find the operator boundary — the first of : = > < ! that isn't inside the
  // field name (field names use letters, digits and dots only).
  const boundary = token.search(/[:=><!]/);
  if (boundary <= 0) return null;

  const field = resolve(token.slice(0, boundary));
  const rest = token.slice(boundary);

  // Order matters: match the longest symbols first.
  const matchers: [string, ConditionOperator][] = [
    [':*', 'is_set'],
    [':_', 'is_empty'],
    ['!:(', 'not_in'],
    [':(', 'in'],
    [':[', 'between'],
    [':^', 'starts_with'],
    [':$', 'ends_with'],
    ['!=', 'not_equals'],
    ['>=', 'after'],
    ['<=', 'before'],
    [':', 'contains'],
    ['=', 'equals'],
    ['>', 'gt'],
    ['<', 'lt'],
  ];

  for (const [sym, operator] of matchers) {
    if (!rest.startsWith(sym)) continue;
    const rawValue = rest.slice(sym.length);

    if (operator === 'is_set' || operator === 'is_empty') {
      return { field, operator, value: null };
    }
    if (operator === 'in' || operator === 'not_in') {
      const inner = rawValue.replace(/\)$/, '');
      const value = inner.split('|').map((s) => unquote(s.trim())).filter(Boolean);
      return { field, operator, value };
    }
    if (operator === 'between') {
      const inner = rawValue.replace(/\]$/, '');
      const value = inner.split('..').map((s) => unquote(s.trim()));
      return { field, operator, value };
    }
    return { field, operator, value: unquote(rawValue) };
  }
  return null;
}

/** Compiles a raw query string into a ConditionTree. */
export function parseQuery(raw: string, fields: ConditionField[]): ConditionTree {
  const trimmed = raw.trim();
  if (!trimmed) return { ...EMPTY_TREE };

  const resolve = fieldResolver(fields);
  const tokens = tokenize(trimmed);

  let conjunction: 'and' | 'or' = 'and';
  const conditions: Condition[] = [];
  for (const token of tokens) {
    const upper = token.toUpperCase();
    if (upper === 'AND') { conjunction = 'and'; continue; }
    if (upper === 'OR') { conjunction = 'or'; continue; }
    const cond = parseToken(token, resolve);
    if (cond) conditions.push(cond);
  }
  return { conjunction, conditions };
}

function quoteIfNeeded(v: string): string {
  return /\s/.test(v) ? `"${v}"` : v;
}

/** Renders a ConditionTree back into the raw query syntax (best-effort). */
export function conditionsToQuery(tree: ConditionTree, fields: ConditionField[]): string {
  const labelByPath = new Map(fields.map((f) => [f.value, f.value]));
  const join = tree.conjunction === 'or' ? ' OR ' : ' AND ';
  return (tree.conditions ?? [])
    .filter((c) => c.field)
    .map((c) => {
      const field = labelByPath.get(c.field) ?? c.field;
      const sym = OP_TO_SYMBOL[c.operator] ?? ':';
      if (c.operator === 'is_set' || c.operator === 'is_empty') return `${field}${sym}`;
      if (c.operator === 'in' || c.operator === 'not_in') {
        const vals = Array.isArray(c.value) ? c.value : [String(c.value ?? '')];
        return `${field}${sym}${vals.map(quoteIfNeeded).join('|')})`;
      }
      if (c.operator === 'between') {
        const vals = Array.isArray(c.value) ? c.value : [];
        return `${field}${sym}${(vals[0] ?? '')}..${(vals[1] ?? '')}]`;
      }
      const v = c.value == null ? '' : String(c.value);
      return `${field}${sym}${quoteIfNeeded(v)}`;
    })
    .join(join);
}
